import { httpsCallable } from '@firebase/functions';
import { functions } from './firebase';
import { createPrefixedLogger } from '@utils/logger';

const log = createPrefixedLogger('invites');

interface InviteValidationResult {
  agentId: string;
  insurerName: string;
}

/**
 * Validate an agent invite code via Cloud Function.
 * Returns agent info if valid, null if not found.
 */
export async function validateInviteCode(code: string): Promise<InviteValidationResult | null> {
  try {
    const callable = httpsCallable<{ code: string }, InviteValidationResult | null>(
      functions,
      'validateInviteCode'
    );
    const result = await callable({ code: code.trim().toUpperCase() });
    return result.data;
  } catch (error) {
    log.error('Failed to validate invite code:', error);
    return null;
  }
}

export interface RedeemInviteCodeResult {
  agentId: string;
  insurerName: string | null;
}

/**
 * Redeem an invite code for the signed-in user. invitedByAgentId and
 * defaultInsurer are create-frozen by Firestore rules, so post-signup
 * linkage has to go through the redeemInviteCode callable (admin SDK).
 * Returns null when the code isn't recognized; throws on other failures
 * (already linked, anonymous account, network) so the screen can surface
 * a specific message.
 */
export async function redeemInviteCode(code: string): Promise<RedeemInviteCodeResult | null> {
  const callable = httpsCallable<{ code: string }, RedeemInviteCodeResult | null>(
    functions,
    'redeemInviteCode'
  );
  const result = await callable({ code: code.trim().toUpperCase() });
  return result.data;
}
